import { useState } from 'react';
import { withFirebaseAuthorization } from '../lib/authFetch';
import { processQueueUrl, queueUrl } from '../lib/apiUrls';
import { normalizeJob, normalizeQueue } from '../lib/normalize';
import { saveStoredProcessing } from '../lib/storage';

const initialQueueForm = {
  type: 'familycopilot_request',
  source: 'familycopilot-web',
  message: 'Please process this uploaded file',
  screen: 'upload',
};

function useQueue() {
  const [queueForm, setQueueForm] = useState(initialQueueForm);
  const [selectedFile, setSelectedFile] = useState(null);
  const [lastQueue, setLastQueue] = useState(null);
  const [lastProcessingJob, setLastProcessingJob] = useState(null);
  const [isSubmittingQueue, setIsSubmittingQueue] = useState(false);
  const [queueStatus, setQueueStatus] = useState({
    type: 'idle',
    message: 'Create a request and send it to the queue.',
  });

  async function handleQueueSubmit(event, currentUser) {
    event.preventDefault();

    if (!currentUser?.userId) {
      setQueueStatus({ type: 'error', message: 'Please sign in before creating a queue request.' });
      return;
    }

    setIsSubmittingQueue(true);
    setLastProcessingJob(null);
    setQueueStatus({
      type: 'loading',
      message: selectedFile ? `Uploading ${selectedFile.name}...` : 'Submitting your request...',
    });

    try {
      const form = new FormData();
      form.append('userId', currentUser.userId);
      form.append('type', queueForm.type);
      form.append('source', queueForm.source);
      form.append('payload', JSON.stringify({ message: queueForm.message, screen: queueForm.screen }));
      if (selectedFile) {
        form.append('file', selectedFile);
      }

      const response = await fetch(queueUrl, await withFirebaseAuthorization({
        method: 'POST',
        body: form,
      }));
      const payload = await parseResponse(response);

      if (!response.ok) {
        throw new Error(payload.error || 'Queue request failed.');
      }

      const createdQueue = normalizeQueue(payload);
      if (!createdQueue?._id) {
        throw new Error('Queue was created but no queue ID was returned.');
      }

      const processResponse = await fetch(processQueueUrl(createdQueue._id), await withFirebaseAuthorization({
        method: 'POST',
      }));
      const processPayload = await parseResponse(processResponse);

      if (!processResponse.ok) {
        throw new Error(processPayload.error || 'Could not start queue processing.');
      }

      const startedJob = normalizeJob(processPayload);
      const startedJobId = startedJob?._id || startedJob?.id || '';
      if (!startedJobId) {
        throw new Error('Queue processing started but no job ID was returned.');
      }

      const nextQueue = {
        ...createdQueue,
        processingJobId: startedJobId,
        status: createdQueue.status || startedJob?.status || 'queued',
      };

      setLastQueue(nextQueue);
      setLastProcessingJob({ _id: startedJobId, status: startedJob?.status || nextQueue.status || 'queued' });
      saveStoredProcessing({ queueId: nextQueue._id, jobId: startedJobId });
      setQueueStatus({ type: 'success', message: `Queue uploaded and processing started: ${startedJobId}.` });
      setSelectedFile(null);
    } catch (error) {
      setQueueStatus({ type: 'error', message: error.message || 'Could not create queue request.' });
    } finally {
      setIsSubmittingQueue(false);
    }
  }

  function handleMessageChange(message) {
    setQueueForm((current) => ({ ...current, message }));
  }

  function handleScreenChange(screen) {
    setQueueForm((current) => ({ ...current, screen }));
  }

  function handleFileChange(file) {
    setSelectedFile(file);
  }

  return {
    queueForm,
    selectedFile,
    lastQueue,
    lastProcessingJob,
    isSubmittingQueue,
    queueStatus,
    handleQueueSubmit,
    handleMessageChange,
    handleScreenChange,
    handleFileChange,
  };
}

async function parseResponse(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

export default useQueue;
